import { useEffect, useMemo, useRef, useState } from "react";
import AdCard, { Ad } from "@/components/AdCard";
import LibraryAdGrid from "@/components/LibraryAdGrid";
import { supabase } from "@/integrations/supabase/client";
import { useLocation, useNavigate } from "react-router-dom";
import { useEntitlements } from "@/entitlements/useEntitlements";
import { usePricingModal } from "@/context/PricingModalContext";
import ObyectLoader from "@/components/ObyectLoader";
import { getStorageUrl } from "@/lib/storage";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Heart } from "lucide-react";
import { logger } from "@/lib/logger";

type AdWithExtras = Ad & {
  createdAt?: string;
  requiresSubscription?: boolean;
};

export default function Favorites() {
  const [ads, setAds] = useState<AdWithExtras[]>([]);
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);

  const location = useLocation();
  const navigate = useNavigate();

  const { isAuthenticated, hasValidAccess, entitlementLoaded } = useEntitlements();
  const { openPricingModal } = usePricingModal();

  useEffect(() => {
    document.title = "Favorites – Saved Ads Library";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "View all ads you've saved as favorites in one place.");
  }, []);

  // Load favorites
  useEffect(() => {
    let active = true;
    load(); // initial fetch only once

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (!active) return;
      // Only reload when login/logout changes
      if (event === "SIGNED_IN" || event === "SIGNED_OUT") {
        load({ keepExisting: true }); // refresh, but keep UI visible
      }
    });

    return () => {
      active = false;
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // ensure we don't set state on an unmounted component
  const safeSet = <T,>(setter: (v: T) => void, value: T) => {
    if (isMounted.current) setter(value);
  };

  // Helper: get/create Favorites collection id
  const getFavoritesId = async (): Promise<string | null> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id;
      if (!uid) return null;

      // 1) try to find user’s Favorites collection
      const { data, error } = await supabase
        .from("user_collections" as any)
        .select("id")
        .eq("user_id", uid)             // ✅ scope to the owner
        .eq("is_system", true)
        .maybeSingle();
      if (error) throw error;
      if ((data as any)?.id) return String((data as any).id);

      // 2) create if missing
      const { data: inserted, error: insErr } = await supabase
        .from("user_collections" as any)
        .insert({ user_id: uid, name: "Favorites", is_system: true })
        .select("id")
        .single();
      if (insErr) throw insErr;
      return String((inserted as any).id);
    } catch (e) {
      logger.error("getFavoritesId failed", e);
      return null;
    }
  };


  const load = async (opts?: { keepExisting?: boolean }) => {
    const keep = !!opts?.keepExisting;
    if (ads.length > 0 && keep) return; // no need to re-fetch
    // Only show skeleton when we have nothing to show yet.
    if (!keep) setLoading(true);
    else logger.debug("Keeping current favorites while refreshing silently");

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setAds([]);
        setLoading(false);
        return;
      }

      const favId = await getFavoritesId();
      if (!favId) {
        setAds([]);
        setLoading(false);
        return;
      }

      const { data: items, error: itemsErr } = await supabase
        .from("user_collection_items" as any)
        .select("ad_id")
        .eq("collection_id", favId);
      if (itemsErr) throw itemsErr;

      const adIds = (items ?? []).map((r: any) => r.ad_id);
      if (adIds.length === 0) {
        setAds([]);
        setLoading(false);
        return;
      }

      const { data: rows, error: adsErr } = await supabase
        .from("ads_with_flags" as any)   // <- keep using this unified view
        .select(`
          id,
          image_path,
          brand_id,
          category_id,
          created_at,
          requires_subscription,
          brand_name,
          brand_logo_path,
          category_name
        `)
        .in("id", adIds)
        .order("created_at", { ascending: false });

      if (adsErr) throw adsErr;

      const mapped = (rows ?? []).map((row: any) => {
        const imageUrl = getStorageUrl("ad-images", row.image_path) ?? "";

        let brandLogoUrl: string | undefined;
        if (row?.brand_logo_path) {
           brandLogoUrl = getStorageUrl("brand-logos", row.brand_logo_path);
        }

        return {
          id: String(row.id),
          brand: row?.brand_name ?? "Unknown brand",
          category: row?.category_name ?? "Unknown category",
          image: imageUrl,
          imagePath: row.image_path,
          brandLogoUrl,
          copy: "",
          start: new Date().toISOString(),
          end: new Date().toISOString(),
          ctr: 0,
          conversions: 0,
          engagement: 0,
          createdAt: row.created_at,
          requiresSubscription: !!row.requires_subscription,
        } as AdWithExtras;
      });

      setAds(mapped);
    } catch (e) {
      logger.error("Failed to load favorites", e);
      setAds([]);
    } finally {
      setLoading(false); // don’t clear existing UI if keepExisting
    }
  };


  useEffect(() => {
    load();
    // refresh on auth change; ensure proper unsubscribe
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      // protect against calling load after unmount
      if (isMounted.current) load();
    });
    return () => subscription?.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const EmptyState = (
    <Card className="p-12 text-center">
      <Heart className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold mb-2">No favorites yet</h3>
      <p className="text-muted-foreground mb-4">
        Start building your collection of inspiring ads
      </p>
      <Button
        onClick={() => navigate('/library')}
        className="bg-gradient-to-r from-[#70BAFF] to-[#258BFF]"
      >
        Browse Library
      </Button>
    </Card>
  );

  return (
    <div className="min-h-screen">

      <main className="mx-auto w-full py-8 animate-fade-in">
        <section className="mb-6 px-3 md:px-6">
          <h1 className="text-2xl font-semibold tracking-tight mt-6">Favorites</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Your saved ads, ready for quick reference and inspiration.
          </p>
        </section>

        {loading && (

          <div className="mt-2 px-3 md:px-6 columns-1 sm:columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-8">
            {/* <div className=" fixed inset-0 z-40 pointer-events-none">
              <div className="min-h-screen  grid place-items-center">
                <div className="ml-64 mb-10  flex flex-col items-center gap-3">
                  <ObyectLoader src="/lovable-uploads/obyect_new.svg" />
                </div>
              </div>
            </div> */}
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-[16px] overflow-hidden bg-[#13171c] mb-6 border-0 ring-1 ring-white/5 shadow-2xl inner-glow">
                <div className="aspect-[4/5] bg-white/5 animate-pulse rounded-[8px] mx-3 mt-3" />
                <div className="p-4 space-y-2">
                  <div className="h-4 w-2/3 bg-white/10 animate-pulse rounded" />
                  <div className="h-3 w-1/2 bg-white/5 animate-pulse rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && ads.length === 0 ? (
          EmptyState
        ) : !loading ? (
          <LibraryAdGrid
            ads={ads}
            pageSizeFree={16}
            hasFiltersApplied={false}
            adsLoading={false}
            bypassSubscriptionCheck={false}
            stateKey={"favorites"}
            layoutMode="masonry"
            rowFirstMasonry          // ✅ same row-first masonry behavior
            preserveOrder
            entitlements={{ isAuthenticated, hasValidAccess, entitlementLoaded }}
            onRequireAuth={() => {
              const next = encodeURIComponent(`${location.pathname}${location.search}`);
              navigate(`/auth/signin?next=${next}`);
            }}
            onRequireSubscribe={() => openPricingModal()}
            renderItem={(ad, shouldBlurFromGrid, { eager }) => (
              <AdCard
                key={ad.id}
                ad={ad}
                showDelete={false}
                onDelete={() => {}}
                showBlur={!!shouldBlurFromGrid}
                onUpgradeClick={openPricingModal}
                eager={!!eager}
              />
            )}
            emptyState={EmptyState}
          />
        ) : null}
      </main>
    </div>
  );
}
